import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxProps, Tooltip } from '@mui/material';
import { GridToolbar, GridValidRowModel } from '@mui/x-data-grid-premium';

import CustomCheckbox, { CheckboxSize } from '@/shared/ui/CustomCheckbox';

import { StyledRadio, disabledCheckboxWrapperStyle } from '../GeneratikTable.styles';
import { DisabledCheckboxTooltip } from '../GeneratikTable.types';
import GeneratikTableErrorOverlay from '../GeneratikTableErrorOverlay';
import { FilterValues } from '../GeneratikTableFilterPanel.types';
import GeneratikTableNoRowsOverlay from '../GeneratikTableNoRowsOverlay';
import { useSelectionCheckboxRow } from './useSelectionCheckboxColumns';

export interface UseGeneratikTableSlotsParams {
  checkboxSize?: CheckboxSize;
  disabledCheckboxTooltip?: DisabledCheckboxTooltip;
  onDisabledCheckboxClick?: () => void;
  handleFilterClick: () => void;
  requiresCampaignPeriod: boolean;
  filterValues: FilterValues;
  emptyStateMessage?: string;
  isError?: boolean;
  onRetry?: () => void;
  hideToolbar: boolean;
  CustomToolbar?: React.ComponentType;
}

export interface UseGeneratikTableSlotsReturn {
  CheckboxSlot: React.ComponentType<CheckboxProps>;
  RadioSlot: React.ComponentType<CheckboxProps>;
  NoRowsOverlay: React.ComponentType;
  ToolbarComponent: React.ComponentType | undefined;
}

const useGeneratikTableSlots = ({
  checkboxSize,
  disabledCheckboxTooltip,
  onDisabledCheckboxClick,
  handleFilterClick,
  requiresCampaignPeriod,
  filterValues,
  emptyStateMessage,
  isError = false,
  onRetry,
  hideToolbar,
  CustomToolbar,
}: UseGeneratikTableSlotsParams): UseGeneratikTableSlotsReturn => {
  const { t } = useTranslation();

  const allRowsDisabled =
    requiresCampaignPeriod &&
    !(filterValues.dateRange as { start?: string; end?: string } | undefined)?.start;

  // Read through a ref so an inline `(row) => ...` tooltip doesn't give the slots a new
  // component identity on every render — that would remount every checkbox in the grid.
  const tooltipRef = useRef(disabledCheckboxTooltip);
  tooltipRef.current = disabledCheckboxTooltip;

  const resolveTooltip = useCallback((row: GridValidRowModel | undefined) => {
    const tooltip = tooltipRef.current;
    if (typeof tooltip !== 'function') return tooltip;
    return row && tooltip(row);
  }, []);

  const CheckboxSlot = useMemo(() => {
    return function CheckboxWithTooltip(props: CheckboxProps) {
      const row = useSelectionCheckboxRow();
      if (props.disabled || allRowsDisabled) {
        // A disabled MUI checkbox is `pointer-events: none`, so the click lands on this wrapper.
        // Only the missing-campaign-period case has somewhere useful to send the user; every other
        // reason a row is unselectable is just a fact about that row, and opening the filter panel
        // there is a non-sequitur. Those wrappers stay inert.
        const handleDisabledClick =
          onDisabledCheckboxClick ?? (allRowsDisabled ? () => handleFilterClick() : undefined);
        return (
          <Tooltip title={resolveTooltip(row) ?? t('Campaign period is required')} arrow>
            <span
              onClick={handleDisabledClick}
              style={disabledCheckboxWrapperStyle}
              data-testid="disabled-selection-checkbox"
            >
              <CustomCheckbox {...props} disabled checkboxSize={checkboxSize} />
            </span>
          </Tooltip>
        );
      }
      return <CustomCheckbox {...props} checkboxSize={checkboxSize} />;
    };
  }, [
    allRowsDisabled,
    checkboxSize,
    handleFilterClick,
    onDisabledCheckboxClick,
    resolveTooltip,
    t,
  ]);

  const RadioSlot = useMemo(() => {
    return function RadioWithTooltip(props: CheckboxProps) {
      const row = useSelectionCheckboxRow();
      if (props.disabled) {
        return (
          <Tooltip title={resolveTooltip(row) ?? t('Campaign period is required')} arrow>
            <span
              onClick={onDisabledCheckboxClick ?? (() => handleFilterClick())}
              data-testid="disabled-selection-checkbox"
            >
              <StyledRadio checked={props.checked} disabled />
            </span>
          </Tooltip>
        );
      }
      return <StyledRadio checked={props.checked} onChange={props.onChange} />;
    };
  }, [handleFilterClick, onDisabledCheckboxClick, resolveTooltip, t]);

  // The grid only mounts `noRowsOverlay` when there are no rows, which is
  // exactly the case a failed fetch produces. Swapping the component (rather
  // than adding a slot) keeps every existing consumer covered without them
  // having to opt into a new overlay.
  const NoRowsOverlay = useMemo(
    () =>
      function NoRowsOverlayWithMessage() {
        if (isError) {
          return <GeneratikTableErrorOverlay onRetry={onRetry} />;
        }
        return <GeneratikTableNoRowsOverlay message={emptyStateMessage} />;
      },
    [emptyStateMessage, isError, onRetry],
  );

  const ToolbarComponent = useMemo(() => {
    if (hideToolbar) return undefined;
    if (CustomToolbar) return CustomToolbar;
    return GridToolbar;
  }, [hideToolbar, CustomToolbar]);

  return { CheckboxSlot, RadioSlot, NoRowsOverlay, ToolbarComponent };
};

export default useGeneratikTableSlots;
